import React, { useMemo, useState } from 'react';
import CPPUtils from 'services/CPPUtils';
import Icon from 'components/Utils/NewIcon';
import ProgressMessage from 'components/Utils/ProgressMessage';
import MiscUtils from 'services/MiscUtils';
import { MediaPreviewModal } from 'components/Patient/CPP/CPPTimeline/CPPTimelineDocument';
import { v4 as uuidV4 } from 'uuid';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useDefaultPharmacy } from 'hooks/useDefaultPharmacy';
import PerformanceUtils from 'services/PerformanceUtils';
import _ from 'lodash';
import { useAppSelector } from 'careand-redux/store';
import { GetStaff, InternOf } from 'careand-redux/selectors';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';
import { setFormValues } from 'hooks/useReduxForm';

const ACTIVE_TAB = 'prescription';

interface CPPTimelinePrescriptionProps {
  prescription: any;
  patient: any;
  setActiveCPPTab: (tab: string) => void;
  items?: React.ReactNode;
}

const CPPTimelinePrescription = ({
  prescription,
  patient,
  ...props
}: CPPTimelinePrescriptionProps) => {
  const dispatch = useAppDispatch();
  const defaultPharmacy = useDefaultPharmacy(patient);

  const [openPreview, setOpenPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>();

  const formName = CPPUtils.getFormName(ACTIVE_TAB, patient.id);
  const formStack = useAppSelector((state) => (state as any).rhfForms[formName]?.stack);

  function editCPPItem(data: any = prescription) {
    props.setActiveCPPTab(ACTIVE_TAB);
    let stack = formStack;
    dispatch(setFormValues({ formName, values: { ...data, stack, attachments: [] } }));
  }

  async function getPrescriptionPDF() {
    if (loading) return;
    if (pdfUrl) {
      setOpenPreview(true);
    } else {
      setLoading(true);
      let result = await dispatch(
        api.HealthRecords.getPrescriptionAttachment({
          resource: { url: prescription.attachments[0] },
          patientId: patient.id,
          prescriptionId: prescription.id,
        }),
        { errorTitle: 'Prescription' },
      );
      setLoading(false);
      if (result) {
        let fileUrl = MiscUtils.getBlobUrl(result);
        setPdfUrl(fileUrl);
        setOpenPreview(true);
      }
    }
  }

  function onPrescriptionClick() {
    if (!prescription.waiting_approval) return getPrescriptionPDF();
    else {
      return editCPPItem();
    }
  }

  const initialPharmacies = useMemo(() => {
    return _.uniqBy(
      [prescription?.pharmacy, defaultPharmacy].filter((pharmacy: any) => !!pharmacy),
      'id',
    );
  }, [defaultPharmacy, prescription]);

  return (
    <>
      <MediaPreviewModal
        openPreview={openPreview}
        setOpenPreview={setOpenPreview}
        url={pdfUrl}
        patient={patient}
        title="Prescription"
        faxOptions={{
          pharmacy: true,
          initialPharmacies,
          fax: CPPUtils.getPharmacyFax(prescription.pharmacy),
        }}
      />
      <div
        className="item-wrapper item-task clickable timeline-prescription"
        onClick={onPrescriptionClick}
      >
        <Content prescription={prescription} editCPPItem={editCPPItem} loading={loading} />
      </div>
    </>
  );
};

const PROGRESS_WRAPPER_STYLE: React.CSSProperties = {
  position: 'absolute',
  right: '5px',
  bottom: '5px',
};
const Content = React.memo(
  ({
    prescription,
    editCPPItem,
    loading,
  }: {
    prescription: any;
    editCPPItem: (data?: any) => void;
    loading: boolean;
  }) => {
    let title = getPrescriptionTitle(prescription);
    let pharmacyName = prescription.pharmacy
      ? [prescription.pharmacy.name, prescription.pharmacy.address?.line1]
          .filter(Boolean)
          .join(', ')
      : undefined;

    return (
      <div className={getRootClassname(prescription)} key={prescription.id}>
        <HoverIcons prescription={prescription} editCPPItem={editCPPItem} />
        <div className="top-bar">
          <Icon src="prescription" />
          <span className="small-title">Prescription</span>
        </div>
        <div className="item-content">
          {!!prescription.waiting_approval && <span className="bold block">[NOT APPROVED]</span>}
          <span>{title}</span>
          {pharmacyName !== undefined && <span className="block">Pharmacy: {pharmacyName}</span>}
          {!!prescription.additional_information && (
            <span className="block mt-2">Comments: {prescription.additional_information}</span>
          )}
        </div>
        <div style={PROGRESS_WRAPPER_STYLE}>
          <ProgressMessage show={loading} />
        </div>
      </div>
    );
  },
);

const HoverIcons = React.memo(
  ({ prescription, editCPPItem }: { prescription: any; editCPPItem: (data?: any) => void }) => {
    const dispatch = useAppDispatch();
    const [deleting, setDeleting] = useState(false);
    const isIntern = !!useAppSelector(InternOf);

    function onShowHide(event: React.MouseEvent) {
      event.stopPropagation();
      let updatedPrescription = {
        ...prescription,
        hide: !prescription.hide,
      };
      dispatch(
        api.HealthRecords.updatePrescription({
          prescription: updatedPrescription,
          patientId: updatedPrescription.patient_id,
          prescriptionId: updatedPrescription.id,
        }),
        { errorTitle: 'Update Prescription' },
      );
    }

    function onRefill(event: React.MouseEvent) {
      event.stopPropagation();
      let data: any = {
        medications: [...prescription.medications],
        additional_information: prescription.additional_information,
        pharmacy: prescription.pharmacy,
      };

      data.medications = data.medications.map((medication: any) => {
        let copy = PerformanceUtils.clone(medication);
        if (copy.as_needed) copy.prn = true;
        copy.id = uuidV4();
        return copy;
      });

      editCPPItem(data);
    }

    async function onDelete(event: React.MouseEvent) {
      event.stopPropagation();
      setDeleting(true);
      await dispatch(
        api.HealthRecords.removePrescription({
          id: prescription.id,
        }),
        { errorTitle: 'Delete Prescription' },
      );
      setDeleting(false);
    }

    const REFILL_STYLE: React.CSSProperties = { position: 'relative', top: '5px', right: '5px' };
    const SHOW_HIDE_STYLE: React.CSSProperties = { position: 'relative', top: '5px', right: '2px' };
    const DELETE_STYLE: React.CSSProperties = { position: 'relative', top: '5px', right: '2px' };

    return (
      <div className="hover-icons">
        {prescription.waiting_approval === undefined ? (
          <>
            <Icon
              height="20px"
              width="20px"
              src="prescription-bottle"
              alt="Refill"
              showTooltip={true}
              //@ts-ignore
              style={REFILL_STYLE}
              className="edit"
              onClick={onRefill}
            />
            <Icon
              height="20px"
              width="20px"
              src={prescription.hide ? 'eye-slash' : 'eye'}
              alt={prescription.hide ? 'Show to patient' : 'Hide from patient'}
              showTooltip={true}
              //@ts-ignore
              style={SHOW_HIDE_STYLE}
              className="edit"
              onClick={onShowHide}
            />
          </>
        ) : (
          !isIntern && (
            <ConfirmationIcon
              height="20px"
              width="20px"
              src="trash"
              alt="Delete prescription"
              showTooltip={true}
              //@ts-ignore
              style={DELETE_STYLE}
              className="trash"
              confirmText="This will delete the prescription. Proceed?"
              loading={deleting}
              onClick={onDelete}
            />
          )
        )}
      </div>
    );
  },
);

function getPrescriptionTitle(prescription: any) {
  return (prescription.medications || []).map((medication: any, index: number) => (
    <div className="medication mb-2" key={`${medication.id}-${index}`}>
      {CPPUtils.getPrescriptionMedicationSummary(medication)}
    </div>
  ));
}

function getRootClassname(prescription: any) {
  let hidden = prescription.hide || prescription.waiting_approval;
  return hidden ? 'greyed-out' : '';
}

export default React.memo(CPPTimelinePrescription);
